import { useEffect } from 'react';

/**
 * useSEO — per-route head management for a CRA SPA.
 *
 * Updates document.title, upserts standard/OG/Twitter/robots meta tags, a canonical
 * link, and injects/removes <script type="application/ld+json"> blocks on mount.
 * All tags written by this hook carry data-managed-seo="1" so they can be cleaned up
 * cleanly when the component unmounts or the route changes.
 */

const SITE = 'https://acodia.co.uk';
const DEFAULT_OG_IMAGE = `${SITE}/images/og/og-default.png`;

function upsertMeta(name, content, useProperty = false) {
  if (content == null || content === '') return;
  const attr = useProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"][data-managed-seo="1"]`);
  if (!el) {
    el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (el) {
      // Don't own tags not owned by us (e.g. charset, viewport)
      el.setAttribute('data-managed-seo', '1');
    } else {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      el.setAttribute('data-managed-seo', '1');
      document.head.appendChild(el);
    }
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"][data-managed-seo="1"]');
  if (!el) {
    el = document.head.querySelector('link[rel="canonical"]');
    if (el) {
      el.setAttribute('data-managed-seo', '1');
    } else {
      el = document.createElement('link');
      el.setAttribute('rel', 'canonical');
      el.setAttribute('data-managed-seo', '1');
      document.head.appendChild(el);
    }
  }
  el.setAttribute('href', href);
}

function injectJsonLd(schemas, key) {
  const scripts = [];
  (schemas || []).forEach((schema, i) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-managed-seo', '1');
    script.setAttribute('data-seo-key', `${key}-${i}`);
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    scripts.push(script);
  });
  return scripts;
}

/**
 * @param {Object} cfg
 * @param {string} cfg.title      — full document.title string
 * @param {string} cfg.description — ≤155-char meta description
 * @param {string} cfg.path       — route path, e.g. '/' or '/pricing'
 * @param {string} [cfg.ogImage]  — absolute URL to OG image (1200×630)
 * @param {string} [cfg.ogType]   — og:type, default 'website'
 * @param {string} [cfg.robots]   — e.g. 'index,follow' or 'noindex,nofollow'
 * @param {Array<Object>} [cfg.jsonLd] — array of JSON-LD schema objects
 */
export function useSEO({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  robots = 'index,follow',
  jsonLd,
} = {}) {
  useEffect(() => {
    const canonical = `${SITE}${path}`;
    if (title) document.title = title;
    upsertMeta('description', description);
    upsertMeta('robots', robots);
    upsertMeta('og:title', title, true);
    upsertMeta('og:description', description, true);
    upsertMeta('og:url', canonical, true);
    upsertMeta('og:type', ogType, true);
    upsertMeta('og:image', ogImage, true);
    upsertMeta('og:site_name', 'Acodia', true);
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', ogImage);
    upsertCanonical(canonical);
    const scripts = injectJsonLd(jsonLd, path);
    return () => {
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [title, description, path, ogImage, ogType, robots, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const SITE_URL = SITE;
